import { Component } from '@theme/component';

// Desktop only: a mouse, and a viewport wider than the theme's mobile breakpoint (so narrow
// windows and the editor's mobile preview keep the touch behaviour: autoplay, no play button).
const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

/**
 * Plays a muted, looping video while it is in view and lazily loads its source.
 *
 * Usage:
 *   <video-autoplay threshold="0.5" sound-group="reels" hover-play>
 *     <video ref="video" data-src="…" poster="…" muted loop playsinline preload="none"></video>
 *     <button ref="soundToggle" on:click="/toggleSound" aria-pressed="false">…</button>
 *     <button ref="playToggle" on:click="/togglePlay" aria-pressed="false">…</button>
 *   </video-autoplay>
 *
 * With `hover-play`, on devices with a mouse the video only plays while the pointer is over it
 * (the source is still preloaded when it scrolls into view); the `playToggle` button pins it so it
 * keeps playing without hovering. Touch devices keep the in-view autoplay behaviour.
 *
 * When a `soundToggle` ref is present, unmuting one video mutes every other `video-autoplay`
 * element that shares the same `sound-group`.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video
 * @property {HTMLButtonElement} [soundToggle]
 * @property {HTMLButtonElement} [playToggle]
 *
 * @extends Component<Refs>
 */
class VideoAutoplay extends Component {
  requiredRefs = ['video'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #abortController = new AbortController();

  #inView = false;
  #hovering = false;
  #pinned = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const { video } = this.refs;

    video.addEventListener('play', () => this.toggleAttribute('data-playing', true), { signal });
    video.addEventListener('pause', () => this.toggleAttribute('data-playing', false), { signal });

    if (this.hoverMode) {
      this.toggleAttribute('data-hover-mode', true);
      this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
      this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    }

    hoverMedia.addEventListener('change', this.#onHoverMediaChange, { signal });

    if (!('IntersectionObserver' in window)) {
      this.#inView = true;
      this.#sync();
      return;
    }

    const threshold = Number(this.getAttribute('threshold')) || 0.5;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            this.#inView = true;
            this.#load();
          } else if (entry.intersectionRatio < threshold) {
            this.#inView = false;
          }
          this.#sync();
        }
      },
      { threshold: [0, threshold, 1], rootMargin: '120px 0px' }
    );
    this.#observer.observe(this);

    window.addEventListener('pagehide', () => this.pause(), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abortController.abort();
    this.pause();
  }

  get video() {
    return this.refs.video;
  }

  /** Hover-to-play applies when the element asks for it and the device has a fine pointer. */
  get hoverMode() {
    return this.hasAttribute('hover-play') && hoverMedia.matches;
  }

  #onHoverMediaChange = () => {
    const hover = this.hoverMode;
    this.toggleAttribute('data-hover-mode', hover);
    if (hover) {
      this.addEventListener('pointerenter', this.#onPointerEnter, { signal: this.#abortController.signal });
      this.addEventListener('pointerleave', this.#onPointerLeave, { signal: this.#abortController.signal });
    } else {
      this.removeEventListener('pointerenter', this.#onPointerEnter);
      this.removeEventListener('pointerleave', this.#onPointerLeave);
      this.#hovering = false;
    }
    this.#sync();
  };

  #onPointerEnter = () => {
    this.#hovering = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovering = false;
    this.#sync();
  };

  /** Plays or pauses according to visibility, hover and the pinned state. */
  #sync() {
    if (!this.#inView) {
      this.pause();
      return;
    }
    if (this.hoverMode && !this.#hovering && !this.#pinned) {
      this.pause();
      return;
    }
    this.play();
  }

  #load() {
    const { video } = this.refs;
    const { src } = video.dataset;

    if (src && !video.getAttribute('src')) {
      video.setAttribute('src', src);
      video.load();
    }
  }

  play() {
    this.#load();
    const promise = this.refs.video.play();
    if (promise?.catch) promise.catch(() => {});
  }

  pause() {
    this.refs.video.pause();
  }

  /**
   * Pins the video so it keeps playing without the pointer over it (hover mode); pressing again
   * unpins and pauses it.
   */
  togglePlay() {
    this.#pinned = !this.#pinned;
    this.toggleAttribute('data-pinned', this.#pinned);
    this.refs.playToggle?.setAttribute('aria-pressed', String(this.#pinned));
    this.#sync();
  }

  /**
   * Toggles the audio of this video and silences every other video in the same sound group.
   */
  toggleSound() {
    const { video } = this.refs;
    const enable = video.muted;

    if (enable) {
      const group = this.getAttribute('sound-group');
      const selector = group ? `video-autoplay[sound-group="${group}"]` : 'video-autoplay';

      for (const other of document.querySelectorAll(selector)) {
        if (other !== this && other instanceof VideoAutoplay) other.mute();
      }
    }

    video.muted = !enable;
    this.#syncSoundState();
    if (enable) this.play();
  }

  mute() {
    this.refs.video.muted = true;
    this.#syncSoundState();
  }

  #syncSoundState() {
    const { video, soundToggle } = this.refs;
    const on = !video.muted;

    this.toggleAttribute('data-sound-on', on);
    soundToggle?.setAttribute('aria-pressed', String(on));
  }
}

if (!customElements.get('video-autoplay')) {
  customElements.define('video-autoplay', VideoAutoplay);
}
